import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Send as SendIcon
} from '@mui/icons-material';
import type { ActivityType } from './useUnifiedActivity';
import MentionInput from '../../../../components/common/MentionInput';
import type { SelectedMention } from '../../../../types/mention';

interface ActivityComposerProps {
  personId: string;
  personName: string;
  activeFilter: ActivityType;
  onCreate: (type: 'note' | 'comment' | 'memory', content: string, mentions: SelectedMention[]) => Promise<void>;
}

/**
 * ActivityComposer - Inline creation for notes, comments, memories
 * Type is controlled by the parent's filter tabs
 */
export const ActivityComposer: React.FC<ActivityComposerProps> = ({
  personId,
  personName,
  activeFilter,
  onCreate
}) => {
  const [content, setContent] = useState('');
  const [selectedMentions, setSelectedMentions] = useState<SelectedMention[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Clear content when filter changes
  useEffect(() => {
    setContent('');
    setSelectedMentions([]);
  }, [activeFilter]);

  const handleMentionChange = (newValue: string, newMentions: SelectedMention[]): void => {
    setContent(newValue);
    setSelectedMentions(newMentions);
  };

  const handleSubmit = async () => {
    if (!content.trim()) return;

    setSubmitting(true);
    try {
      await onCreate(activeFilter as 'note' | 'comment' | 'memory', content.trim(), selectedMentions);
      setContent('');  // Clear on success
      setSelectedMentions([]);
    } catch (err) {
      console.error('Failed to create activity:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const getPlaceholder = () => {
    switch (activeFilter) {
      case 'note':
        return `Add a note about ${personName}...`;
      case 'comment':
        return `Comment on ${personName}'s profile...`;
      case 'memory':
        return `Share a memory of ${personName}...`;
      default:
        return `Add a ${activeFilter}...`;
    }
  };

  // Get content type based on filter
  const getContentType = (): 'note' | 'comment' | 'memory' => {
    if (activeFilter === 'all') return 'comment';
    return activeFilter as 'note' | 'comment' | 'memory';
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <MentionInput
          value={content}
          onChange={handleMentionChange}
          placeholder={getPlaceholder()}
          disabled={submitting}
          multiline
          minRows={3}
          maxRows={8}
          fullWidth
          sx={{ mb: 2 }}
          contentType={getContentType()}
          targetId={personId}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
            onClick={handleSubmit}
            disabled={!content.trim() || submitting}
          >
            {submitting ? 'Posting...' : 'Post'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
